import { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import {
  CLIENT_CODES, SUGGESTED_DEFAULTS, listProfiles, createDraftProfile, activateProfile,
  deactivateProfile, updateProfileFields
} from "../services/financeCore/clientTaxProfileService";
import { WHT_MODELS } from "../services/financeCore/whtCalculator";
import { GST_SCOPES } from "../services/financeCore/gstCalculator";

const CLIENT_LIST = Object.values(CLIENT_CODES);

function emptyForm(clientCode) {
  const defaults = SUGGESTED_DEFAULTS[clientCode] || {};
  return {
    clientCode,
    clientName: defaults.clientName || clientCode,
    whtModel: defaults.whtModel || WHT_MODELS.SERVICE_CHARGES_PLUS_GST,
    whtRate: defaults.whtRate ?? 0.07,
    gstRate: defaults.gstRate ?? 0.16,
    gstScope: defaults.gstScope || GST_SCOPES.SERVICE_CHARGES_ONLY,
    gstWithholdingRate: defaults.gstWithholdingRate ?? 0.20,
    recoveryMethod: defaults.recoveryMethod || "NONE",
    recoveryRate: defaults.recoveryRate ?? "",
    recoveryVarianceTolerance: 5,
    paymentTermsDays: 30
  };
}

function formFromProfile(profile) {
  return {
    clientCode: profile.clientCode,
    clientName: profile.clientName,
    whtModel: profile.whtModel,
    whtRate: profile.whtRate,
    gstRate: profile.gstRate,
    gstScope: profile.gstScope,
    gstWithholdingRate: profile.gstWithholdingRate,
    recoveryMethod: profile.recoveryMethod,
    recoveryRate: profile.recoveryRate ?? "",
    recoveryVarianceTolerance: profile.recoveryVarianceTolerance,
    paymentTermsDays: profile.paymentTermsDays ?? 30
  };
}

const CAN_ACTIVATE = ["DRAFT", "INACTIVE"];

export default function ClientTaxProfileAdmin() {
  const [selectedClient, setSelectedClient] = useState(CLIENT_LIST[0]);
  const [form, setForm] = useState(emptyForm(CLIENT_LIST[0]));
  const [editingProfileId, setEditingProfileId] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const refresh = async (clientCode) => {
    setLoading(true);
    try {
      setProfiles(await listProfiles(clientCode));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setForm(emptyForm(selectedClient));
    setEditingProfileId(null);
    refresh(selectedClient);
  }, [selectedClient]);

  const update = (field) => (event) => {
    const value = event.target.value;
    setForm((current) => ({ ...current, [field]: value }));
  };

  const startEdit = (profile) => {
    setEditingProfileId(profile.id);
    setForm(formFromProfile(profile));
    setMessage("");
  };

  const cancelEdit = () => {
    setEditingProfileId(null);
    setForm(emptyForm(selectedClient));
    setMessage("");
  };

  const create = async () => {
    setMessage("");
    try {
      await createDraftProfile({
        ...form,
        recoveryRate: form.recoveryRate === "" ? null : Number(form.recoveryRate)
      });
      setMessage(`Draft tax profile created for ${form.clientCode}. Review it below, then activate when ready.`);
      await refresh(selectedClient);
    } catch (error) {
      setMessage(error.message || "Could not create the tax profile.");
    }
  };

  const saveEdit = async () => {
    setMessage("");
    try {
      await updateProfileFields(editingProfileId, form);
      setMessage("Tax profile updated. Already-approved invoices are unaffected — this only changes future approvals.");
      setEditingProfileId(null);
      setForm(emptyForm(selectedClient));
      await refresh(selectedClient);
    } catch (error) {
      setMessage(error.message || "Could not update the tax profile.");
    }
  };

  const activate = async (profileId) => {
    setMessage("");
    try {
      await activateProfile(profileId);
      setMessage("Tax profile activated.");
      await refresh(selectedClient);
    } catch (error) {
      setMessage(error.message || "Could not activate the tax profile.");
    }
  };

  const deactivate = async () => {
    if (!window.confirm(`Deactivate the current tax profile for ${selectedClient}? WHT/GST will record as 0 on new approvals until another one is activated.`)) return;
    setMessage("");
    try {
      await deactivateProfile(selectedClient);
      setMessage(`Tax profile deactivated for ${selectedClient}.`);
      await refresh(selectedClient);
    } catch (error) {
      setMessage(error.message || "Could not deactivate the tax profile.");
    }
  };

  return (
    <div className="app-shell">
      <Sidebar />
      <main className="main-content">
        <header className="page-header">
          <div>
            <div className="eyebrow">Management Finance</div>
            <h1 className="page-title">Client Tax Profiles</h1>
            <p className="page-copy">
              Every approved invoice snapshot uses whichever profile is ACTIVE for its client at approval time.
              Creating a profile never changes past invoices — activate it explicitly when ready.
            </p>
          </div>
        </header>

        <section className="panel">
          <div className="panel-header">
            <h2 className="panel-title">{editingProfileId ? `Editing ${form.clientCode} Profile` : "Create a Draft Profile"}</h2>
          </div>
          <div className="panel-body">
            <div className="form-grid">
              <div className="field">
                <label>Client</label>
                <select className="input" value={selectedClient} disabled={!!editingProfileId} onChange={(e) => setSelectedClient(e.target.value)}>
                  {CLIENT_LIST.map((code) => <option key={code} value={code}>{code}</option>)}
                </select>
              </div>
              <div className="field">
                <label>WHT Model</label>
                <select className="input" value={form.whtModel} onChange={update("whtModel")}>
                  <option value={WHT_MODELS.SERVICE_CHARGES_PLUS_GST}>Service Charges + GST</option>
                  <option value={WHT_MODELS.GROSS_INVOICE_WITH_RECOVERY}>Gross Invoice with Recovery</option>
                </select>
              </div>
              <div className="field">
                <label>WHT Rate (e.g. 0.07)</label>
                <input className="input" type="number" step="0.0001" value={form.whtRate} onChange={update("whtRate")} />
              </div>
              <div className="field">
                <label>GST Rate</label>
                <input className="input" type="number" step="0.01" value={form.gstRate} onChange={update("gstRate")} />
              </div>
              <div className="field">
                <label>GST Scope</label>
                <select className="input" value={form.gstScope} onChange={update("gstScope")}>
                  <option value={GST_SCOPES.SERVICE_CHARGES_ONLY}>Service Charges Only</option>
                  <option value={GST_SCOPES.FULL_INVOICE}>Full Invoice</option>
                </select>
              </div>
              <div className="field">
                <label>GST Withholding Rate</label>
                <input className="input" type="number" step="0.01" value={form.gstWithholdingRate} onChange={update("gstWithholdingRate")} />
              </div>
              <div className="field">
                <label>Recovery Method</label>
                <select className="input" value={form.recoveryMethod} onChange={update("recoveryMethod")}>
                  <option value="NONE">None</option>
                  <option value="GROSS_UP_ADD_BACK">Gross-up Add-back</option>
                </select>
              </div>
              <div className="field">
                <label>Recovery Rate (optional)</label>
                <input className="input" type="number" step="0.000001" value={form.recoveryRate} onChange={update("recoveryRate")} placeholder="e.g. 0.088376" />
              </div>
              <div className="field">
                <label>Recovery Variance Tolerance (PKR)</label>
                <input className="input" type="number" value={form.recoveryVarianceTolerance} onChange={update("recoveryVarianceTolerance")} />
              </div>
              <div className="field">
                <label>Payment Terms (days)</label>
                <input className="input" type="number" value={form.paymentTermsDays} onChange={update("paymentTermsDays")} />
              </div>
            </div>
            <div className="button-row">
              {editingProfileId ? (
                <>
                  <button type="button" className="btn btn-primary" onClick={saveEdit}>Save Changes</button>
                  <button type="button" className="btn btn-muted" onClick={cancelEdit}>Cancel</button>
                </>
              ) : (
                <button type="button" className="btn btn-primary" onClick={create}>Create Draft</button>
              )}
            </div>
            {message && <p className="page-copy" role="status">{message}</p>}
          </div>
        </section>

        <section className="panel">
          <div className="panel-header">
            <h2 className="panel-title">{selectedClient} — Profile Versions</h2>
          </div>
          <div className="panel-body">
            {loading ? <p className="page-copy">Loading…</p> : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Version</th><th>Status</th><th>WHT Model</th><th>WHT Rate</th><th>Recovery Rate</th><th>Effective From</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {profiles.sort((a, b) => b.version - a.version).map((profile) => (
                    <tr key={profile.id}>
                      <td>{profile.version}</td>
                      <td><span className="badge">{profile.status}</span></td>
                      <td>{profile.whtModel}</td>
                      <td>{profile.whtRate}</td>
                      <td>{profile.recoveryRate ?? "—"}</td>
                      <td>{profile.effectiveFrom}</td>
                      <td className="button-row" style={{ margin: 0, padding: 0, borderTop: 0 }}>
                        {CAN_ACTIVATE.includes(profile.status) && (
                          <button type="button" className="btn btn-success" onClick={() => activate(profile.id)}>Activate</button>
                        )}
                        {profile.status === "ACTIVE" && (
                          <button type="button" className="btn btn-danger" onClick={deactivate}>Deactivate</button>
                        )}
                        <button type="button" className="btn btn-muted" onClick={() => startEdit(profile)}>Edit</button>
                      </td>
                    </tr>
                  ))}
                  {!profiles.length && <tr><td colSpan={7}>No tax profiles yet for {selectedClient}.</td></tr>}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
